import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const featureLabels = {
  oneview: 'Strata Record Synthesis',
  observer_triangulation: 'Observer Triangulation',
  narrative_prompting: 'Narrative Prompting',
  role_fit: 'Role Fit Analysis',
  succession_readiness: 'Succession Readiness',
  proof: 'Proof Document',
  proof_bundle: 'Proof Bundle',
  re_synthesis: 'Re-Synthesis',
  expert_review: 'Expert Review',
  annual_review: 'Annual Review'
};

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [feature, setFeature] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setFeature(data.feature);
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const getRedirectPath = () => {
    if (!feature) return '/dashboard';
    if (feature === 'oneview') return '/insights';
    if (feature === 'role_fit' || feature === 'proof' || feature === 'proof_bundle') return '/proof';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-4">
      <Card className="strata-card max-w-md w-full">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-amber-500 animate-spin mb-4" />
              <h2 className="font-display text-2xl text-white mb-2">Verifying Payment</h2>
              <p className="text-slate-400">Please wait while we confirm your payment...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="font-display text-2xl text-white mb-2">Payment Successful!</h2>
              <p className="text-slate-400 mb-6">
                {feature && featureLabels[feature] 
                  ? `${featureLabels[feature]} has been unlocked.`
                  : 'Your purchase has been completed.'
                }
              </p>
              <Button 
                onClick={() => navigate(getRedirectPath())}
                className="strata-button-primary"
                data-testid="continue-btn"
              >
                Continue
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <span className="text-red-500 text-2xl">!</span>
              </div>
              <h2 className="font-display text-2xl text-white mb-2">Verification Failed</h2>
              <p className="text-slate-400 mb-6">
                We couldn't verify your payment. If you were charged, please contact support.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="strata-button-secondary"
              >
                Return to Dashboard
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
